import { c as _c } from "react/compiler-runtime";
import { NamespaceMapper, TFunctionType, getTFuncWithNamespace, getUseLocoTranslation } from '@systemeio/util';
import { TOptions } from 'i18next';
import { FormatXMLElementFn, PrimitiveType } from 'intl-messageformat';
import { i18n, useTranslation } from 'next-i18next/pages';
import { useCallback, useMemo } from 'react';
import { getBrand } from 'shared/utils/brand/get-brand';
import { formUrl } from 'shared/utils/links/form-url';
import { useTranslation as useTranslationReact } from 'react-i18next';
import LocalizeNamespaces from 'shared/constants/localize-namespaces.mjs';
export type LocalizeKeys = keyof typeof LocalizeNamespaces;
export type LocalizeValues = typeof LocalizeNamespaces[LocalizeKeys];
export type TLocoType = ReturnType<typeof getTFuncWithNamespace>;
const namespaceMapper: NamespaceMapper = (assetKey: string) => {
  if (assetKey === undefined) return undefined;
  const basePart = assetKey.split('.')[0];
  switch (basePart) {
    case 'global':
    case 'core':
      return [LocalizeNamespaces.global];
    case 'header':
      return [LocalizeNamespaces.header];
    case 'confirmation_modal':
      return [LocalizeNamespaces.confirmation];
    case 'currency':
      return [LocalizeNamespaces.currency];
    case 'locale':
      return [LocalizeNamespaces.locale];
    case 'dashboard':
      {
        const namespaceFromKey = assetKey.split('.')[1];
        return [LocalizeNamespaces[namespaceFromKey as LocalizeKeys], ...Object.values(LocalizeNamespaces)];
      }
    default:
      return undefined;
  }
};
const defaultNamespaces = [LocalizeNamespaces.global, LocalizeNamespaces.header, LocalizeNamespaces.notifications, LocalizeNamespaces.locale];
const getGlobalVariables = () => ({
  brandName: getBrand().name,
  mainWebsiteUrl: formUrl('', false),
  dashboardUrl: formUrl('')
});
export const tWithNamespace = (assetKey: string, options?: TOptions) => {
  if (!i18n?.t) return '';
  return getTFuncWithNamespace(i18n.t as unknown as TFunctionType, namespaceMapper)(assetKey, {
    ...getGlobalVariables(),
    ...options
  });
};
const useLocoTranslationHook = getUseLocoTranslation({
  useTranslation: useTranslation as unknown as typeof useTranslationReact,
  defaultNamespaces,
  namespaceMapper
});
export const useLocoTranslation: typeof useLocoTranslationHook = () => {
  const $ = _c(13);
  const t0 = useLocoTranslationHook();
  let rest;
  let tICUTemp;
  let tTemp;
  if ($[0] !== t0) {
    ({
      t: tTemp,
      tICU: tICUTemp,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = rest;
    $[2] = tICUTemp;
    $[3] = tTemp;
  } else {
    rest = $[1];
    tICUTemp = $[2];
    tTemp = $[3];
  }
  let t1;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = getGlobalVariables();
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const globalVariables = t1;
  let t2;
  if ($[5] !== tTemp) {
    t2 = (assetKey, options) => tTemp(assetKey, {
      ...globalVariables,
      ...options
    });
    $[5] = tTemp;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const t = t2;
  let t3;
  if ($[7] !== tICUTemp) {
    t3 = (assetKey_0, values, options_0) => tICUTemp(assetKey_0, values, {
      ...globalVariables,
      ...options_0
    });
    $[7] = tICUTemp;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const tICU = t3;
  let t4;
  if ($[9] !== rest || $[10] !== t || $[11] !== tICU) {
    t4 = {
      t,
      tICU,
      ...rest
    };
    $[9] = rest;
    $[10] = t;
    $[11] = tICU;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  return t4;
};